import { Check } from 'lucide-react';
import { CourseModuleUserTreeNode } from '../../../../lib/interfaces/courses';
import CourseCover from '../course-cover/course-cover';

export interface CourseTocProps {
  title: string;
  imageUrl: string | null;
  percentage: number;
  modules: CourseModuleUserTreeNode[];
  currentLessonId: string | null;
  onSelectLesson: (lessonId: string) => void;
}

/** Small check box on the right of each row — filled gradient when the lesson is done. */
const CompletionMark = ({ done }: { done: boolean }) =>
  done ? (
    <span
      className="w-5 h-5 rounded-md flex items-center justify-center text-white shrink-0"
      style={{ background: 'linear-gradient(135deg, #5FD5DE 0%, #C0DE60 100%)' }}>
      <Check className="w-3.5 h-3.5" strokeWidth={3} />
    </span>
  ) : (
    <span className="w-5 h-5 rounded-md border border-[#E4E7EC] shrink-0" aria-hidden="true" />
  );

/**
 * Table of contents for a course: a progress header plus the module/lesson tree. Each lesson is
 * selectable and shows its completion; the current one is highlighted. Rendered beside the player.
 */
const CourseToc = ({ title, imageUrl, percentage, modules, currentLessonId, onSelectLesson }: CourseTocProps) => {
  const allLessons = modules.flatMap((module) => module.lessons);
  const doneCount = allLessons.filter((lesson) => lesson.isCompleted).length;

  return (
    <div
      className="bg-white rounded-2xl border border-[#E4E7EC] flex flex-col h-full min-h-0"
      style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
      <div className="flex items-center gap-4 p-5 border-b border-[#E4E7EC] shrink-0">
        <CourseCover imageUrl={imageUrl} size="md" percentage={percentage} alt={title} />
        <div className="min-w-0">
          <h2 className="text-[16px] font-bold text-[#12282A] truncate">{title}</h2>
          <p className="text-[13px] text-[#888]">
            {percentage}% · {doneCount} von {allLessons.length} Lektionen
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-4">
        {modules.map((module) => (
          <div key={module._id} className="flex flex-col gap-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#888] px-2">{module.title}</p>
            {module.lessons.map((lesson) => {
              const isCurrent = lesson._id === currentLessonId;
              return (
                <button
                  key={lesson._id}
                  type="button"
                  onClick={() => onSelectLesson(lesson._id)}
                  aria-current={isCurrent}
                  className={`flex items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors ${
                    isCurrent ? 'bg-[#F0F4FC]' : 'hover:bg-[#F0F4FC]'
                  }`}>
                  <span className={`flex-1 min-w-0 truncate text-[14px] ${isCurrent ? 'font-bold text-[#12282A]' : 'text-[#12282A]'}`}>
                    {lesson.title}
                  </span>
                  <CompletionMark done={lesson.isCompleted} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourseToc;
